import { useMemo, useState } from "react";
import {
  answerCatalogFor,
  answerChapterOf,
  answerSortRank,
  type AnswerChapter
} from "../../shared/answer-catalog";
import { answerBody, answerQuestion } from "../staff-answers";
import { noteParagraphs } from "../staff-feedback";
import { voiceNoteState } from "../voice-note";
import { makeSafeT } from "./core";
import { staffAudioUrl } from "./FeedbackNotes";
import type { StaffPaneProps } from "./console-types";
import "./answers.css";

/**
 * F138b: the partner's reading surface for what the client answered.
 *
 * What it replaced: 63 cards, 12 088 px of muted text in one column, no
 * numbering, no chapters, no search, no way to fold a long answer - and, for
 * 33 answers the client DICTATED, no way to hear the recording at all. Every
 * part of that already existed (the id and the transcript ride the detail
 * payload, the byte route is live and tenant-scoped, StaffVoiceNote plays the
 * same recordings in the feedback pane); only this pane never rendered them.
 *
 * The reading contract, in the audit's words: the transcript is the answer,
 * because it reads faster; the recording sits under it as the check on tone
 * and on what the transcript lost. An answer with no recording gets no player,
 * so no card carries a control that cannot do anything (F44).
 */

/**
 * Roughly three lines of the answer column at 1440 px. The feedback pane folds
 * at 900 (twelve lines), which is right for ONE note the partner opened on
 * purpose and wrong for sixty-three answers read in a row.
 */
export const ANSWER_FOLD_CHARS = 260;

export interface AnswerVoice {
  audioUrl: string;
  /** "text" = a transcript exists and IS the body; the rest name what is missing. */
  state: ReturnType<typeof voiceNoteState>;
}

export interface AnswerCardModel {
  id: string;
  itemKey: string;
  questionnaire: string;
  chapter: AnswerChapter | null;
  /** The question's own place in ITS questionnaire, kept under every filter. */
  number: number;
  label: string;
  legacySlug: string | null;
  body: string;
  /** False when `answerBody` found nothing readable, so nothing is printed. */
  hasBody: boolean;
  answered: boolean;
  voice: AnswerVoice | null;
  followUps: Array<{ field: string; audioUrl: string }>;
  links: string[];
  photos: string[];
  haystack: string;
}

/** Diacritics-insensitive, because nobody types "Buddyho" with the right ý. */
export function normalizeSearch(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

function answerValue(row: Record<string, unknown>): Record<string, unknown> {
  return (row.value && typeof row.value === "object" ? row.value : {}) as Record<string, unknown>;
}

/**
 * "Answered" is not guessed: it asks the SAME function that renders the card
 * whether it found anything to print. `answerBody` returns the empty marker
 * when a stored row carries nothing readable, and those rows are exactly the
 * ones the audit found mixed in among real answers.
 */
export function buildAnswerCards(
  rows: Array<Record<string, unknown>>,
  clientId: string,
  t: (key: string) => string
): AnswerCardModel[] {
  const safe = makeSafeT(t);
  const emptyMarker = t("staffConsole.answerEmpty");
  const numbering = new Map<string, number>();
  return [...rows]
    .sort((left, right) => answerSortRank(left) - answerSortRank(right))
    .map((row) => {
      const questionnaire = String(row.questionnaire);
      const itemKey = String(row.itemKey);
      const value = answerValue(row);
      const question = answerQuestion(itemKey, t);
      const body = answerBody(row, value, t);
      const transcript = typeof row.transcript === "string" && row.transcript.trim() ? row.transcript.trim() : null;
      const attachmentId = typeof row.attachmentId === "string" && row.attachmentId ? row.attachmentId : null;
      const nextNumber = (numbering.get(questionnaire) ?? 0) + 1;
      numbering.set(questionnaire, nextNumber);
      const followUps = Object.entries(value).flatMap(([field, item]) => (
        field.endsWith("_recording") && typeof item === "string" && item
          ? [{
              field: field.slice(0, -"_recording".length),
              audioUrl: staffAudioUrl(clientId, { audioAttachmentId: item }) ?? ""
            }]
          : []
      )).filter((entry) => entry.audioUrl !== "");
      return {
        id: `${questionnaire}:${itemKey}`,
        itemKey,
        questionnaire,
        chapter: answerChapterOf(row),
        number: nextNumber,
        label: question.label,
        legacySlug: question.legacySlug,
        body,
        hasBody: body !== emptyMarker,
        // 🔴 A recording IS an answer. A dictated answer whose transcript is
        // still running (or failed) has no readable body, so the first draft
        // filed it under "nezodpovězené" - which tells the partner the client
        // said nothing while the client's own voice sits in R2. Caught by
        // looking at the fixture, not by a test.
        answered: body !== emptyMarker || attachmentId !== null,
        voice: attachmentId
          ? {
              audioUrl: staffAudioUrl(clientId, { audioAttachmentId: attachmentId }) ?? "",
              state: voiceNoteState({ text: transcript, hasAudio: true, transcriptStatus: typeof row.transcriptStatus === "string" ? row.transcriptStatus : null })
            }
          : null,
        followUps,
        links: Array.isArray(value.links) ? value.links.map(String) : [],
        photos: Array.isArray(value.attachments)
          ? (value.attachments as Array<Record<string, unknown>>).flatMap((item) => (typeof item?.id === "string" ? [item.id] : []))
          : [],
        haystack: normalizeSearch([question.label, question.legacySlug ?? "", body, safe(`sections.${questionnaire}.title`, questionnaire)].join(" "))
      };
    });
}

export interface AnswerChapterGroup {
  questionnaire: string;
  chapter: AnswerChapter | null;
  anchor: string;
  cards: AnswerCardModel[];
}

/**
 * Chapters follow the questionnaire's own blocks, in the questionnaire's own
 * order (shared/answer-catalog.ts). A card whose key the catalog no longer
 * knows keeps a chapter of its own at the end instead of being folded into
 * somebody else's block.
 */
export function groupIntoChapters(cards: AnswerCardModel[]): AnswerChapterGroup[] {
  const groups: AnswerChapterGroup[] = [];
  for (const card of cards) {
    const last = groups[groups.length - 1];
    if (last && last.questionnaire === card.questionnaire && last.chapter === card.chapter) {
      last.cards.push(card);
      continue;
    }
    groups.push({
      questionnaire: card.questionnaire,
      chapter: card.chapter,
      anchor: `answers-${card.questionnaire}-${card.chapter ?? "legacy"}`,
      cards: [card]
    });
  }
  return groups;
}

function chapterTitle(group: AnswerChapterGroup, t: (key: string) => string): string {
  const safe = makeSafeT(t);
  if (!group.chapter) return t("staffConsole.answers.legacyChapter");
  return safe(`questionnaire.groups.${group.chapter}.title`, group.chapter);
}

/**
 * A long answer folds to its first lines with a button, never a <details>: the
 * console's disclosure grammar is the note fold of the feedback pane, and a
 * nested drawer is exactly what F138 took out of this console.
 */
export function AnswerText(props: { text: string; t: (key: string) => string }) {
  const [open, setOpen] = useState(false);
  const paragraphs = noteParagraphs(props.text);
  const long = props.text.length > ANSWER_FOLD_CHARS;
  const shown = long && !open
    ? (() => {
        const head: string[] = [];
        let budget = ANSWER_FOLD_CHARS;
        for (const paragraph of paragraphs) {
          if (budget <= 0) break;
          head.push(paragraph.length > budget ? `${paragraph.slice(0, budget).trimEnd()}…` : paragraph);
          budget -= paragraph.length;
        }
        return head;
      })()
    : paragraphs;
  return (
    <div className="answers-text" data-answer-folded={long && !open ? "true" : "false"}>
      {shown.map((paragraph, index) => (
        <p className="human-answer-body" key={index}>{paragraph}</p>
      ))}
      {long && (
        <button className="quiet-link answers-more" type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {props.t(open ? "staffConsole.material.noteCollapse" : "staffConsole.material.noteExpand")}
        </button>
      )}
    </div>
  );
}

function AnswerCard(props: { card: AnswerCardModel; clientId: string; t: (key: string) => string }) {
  const { card, t } = props;
  const safe = makeSafeT(t);
  return (
    <article className="staff-card human-answer" data-answer-voice={card.voice ? "true" : "false"} data-answer-key={card.itemKey}>
      <p className="human-answer-question">
        <span className="answers-number" aria-hidden="true">{card.number}</span>
        {card.label}
        {card.legacySlug && <small className="human-answer-key">{card.legacySlug}</small>}
      </p>
      {/* "Bez odpovědi" over a card that carries the client's own voice is a
          card saying two opposite things; the state sentence below is the
          answer until the transcript lands. */}
      {card.hasBody && <AnswerText text={card.body} t={t} />}
      {card.voice && (
        <div className="answers-voice">
          {/* The transcript is the answer; this line only speaks when there is
              no transcript to read, and then it says which of the three
              truths it is instead of a dead "Hlasová odpověď" label. */}
          {card.voice.state !== "text" && (
            <p className="answers-voice-state">{t(`staffConsole.answers.${card.voice.state === "transcribing" ? "voiceTranscribing" : card.voice.state === "failed" ? "voiceFailed" : "voiceNoSpeech"}`)}</p>
          )}
          <audio
            className="answers-audio"
            controls
            preload="none"
            src={card.voice.audioUrl}
            aria-label={t("staffConsole.answers.playLabel")}
          />
        </div>
      )}
      {card.followUps.length > 0 && (
        <div className="answers-followups">
          {card.followUps.map((entry) => (
            <div className="answers-followup" key={entry.field}>
              {/* Never the raw field name: `promo_detail` on a partner screen
                  is internal code, which principle 5 of F138 forbids. Every
                  field FOLLOWUP_VOICE_FIELDS really carries has a label; the
                  fallback is for a field added tomorrow. */}
              <span className="answers-followup-label">{safe(`questionnaire.fields.${entry.field}`, t("staffConsole.answers.followUpLabel"))}</span>
              <audio className="answers-audio" controls preload="none" src={entry.audioUrl} aria-label={t("staffConsole.answers.playLabel")} />
            </div>
          ))}
        </div>
      )}
      {card.links.length > 0 && (
        <p className="human-answer-links">
          {card.links.map((link) => (
            <a key={link} className="quiet-link break-text" href={link} target="_blank" rel="noreferrer">{link}</a>
          ))}
        </p>
      )}
      {card.photos.length > 0 && (
        <div className="human-answer-photos">
          {card.photos.map((id) => (
            <a key={id} href={`/api/staff/clients/${props.clientId}/attachments/${id}`} target="_blank" rel="noreferrer">
              <img src={`/api/staff/clients/${props.clientId}/attachments/${id}`} alt="" loading="lazy" />
            </a>
          ))}
        </div>
      )}
    </article>
  );
}

export function AnswersView(props: StaffPaneProps & { questionnaire?: "q1" | "q2" }) {
  const { t } = props;
  const clientId = props.detail.client.id;
  const [query, setQuery] = useState("");
  const [voiceOnly, setVoiceOnly] = useState(false);

  const rows = useMemo(() => (
    props.questionnaire
      ? props.detail.answers.filter((row) => String(row.questionnaire) === props.questionnaire)
      : props.detail.answers
  ), [props.detail.answers, props.questionnaire]);

  const cards = useMemo(() => buildAnswerCards(rows, clientId, t), [rows, clientId, t]);
  const answered = cards.filter((card) => card.answered);
  const unanswered = cards.filter((card) => !card.answered);
  const voiceTotal = answered.filter((card) => card.voice).length;

  const needle = normalizeSearch(query.trim());
  const visible = answered.filter((card) => (
    (!voiceOnly || card.voice !== null) && (needle === "" || card.haystack.includes(needle))
  ));
  const chapters = groupIntoChapters(visible);
  const filtered = voiceOnly || needle !== "";

  const tocQuestionnaires = [...new Set(chapters.map((group) => group.questionnaire))];
  const title = props.questionnaire
    ? makeSafeT(t)(`sections.${props.questionnaire}.title`, t("staffConsole.answers.title"))
    : t("staffConsole.answers.title");

  if (cards.length === 0) return <p className="staff-empty">{t("staffConsole.emptyAnswers")}</p>;

  return (
    <section className="answers-view" aria-labelledby="answers-title">
      <header className="answers-head">
        <p className="panel-label">{t("staffConsole.answers.kicker")}</p>
        <h3 id="answers-title">{title}</h3>
        <p className="answers-lead">
          {t("staffConsole.answers.lead")
            .replace("{answers}", String(answered.length))
            .replace("{voice}", String(voiceTotal))}
        </p>
      </header>

      <div className="answers-toolbar">
        <span className="staff-search answers-search">
          <span className="staff-search-field">
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="6.5" />
              <path d="m16 16 4 4" />
            </svg>
            <input
              aria-label={t("staffConsole.answers.searchLabel")}
              placeholder={t("staffConsole.answers.searchPlaceholder")}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </span>
        </span>
        {/* A picked filter is not a work state, so it wears the console's one
            selection grammar (blue tint), never amber (audit P-15). */}
        <div className="note-filter-row" role="group" aria-label={t("staffConsole.answers.filterLabel")}>
          <button
            type="button"
            className={`note-filter-chip${voiceOnly ? "" : " active"}`}
            aria-pressed={!voiceOnly}
            onClick={() => setVoiceOnly(false)}
          >
            {t("staffConsole.answers.filterAll")}
            <span className="note-filter-count">{answered.length}</span>
          </button>
          <button
            type="button"
            className={`note-filter-chip${voiceOnly ? " active" : ""}`}
            aria-pressed={voiceOnly}
            onClick={() => setVoiceOnly(true)}
          >
            {t("staffConsole.answers.filterVoice")}
            <span className="note-filter-count">{voiceTotal}</span>
          </button>
        </div>
        <p className="answers-count" role="status">
          {t("staffConsole.answers.visible")
            .replace("{shown}", String(visible.length))
            .replace("{total}", String(answered.length))}
        </p>
      </div>

      {chapters.length > 0 && (
        <nav className="answers-toc" aria-label={t("staffConsole.answers.tocLabel")}>
          {tocQuestionnaires.map((questionnaire) => (
            <div className="answers-toc-block" key={questionnaire}>
              {tocQuestionnaires.length > 1 && (
                <p className="answers-toc-title">{makeSafeT(t)(`sections.${questionnaire}.title`, questionnaire)}</p>
              )}
              <ol className="answers-toc-list">
                {chapters.filter((group) => group.questionnaire === questionnaire).map((group) => (
                  <li key={group.anchor}>
                    <a className="answers-toc-link" href={`#${group.anchor}`}>
                      {chapterTitle(group, t)}
                      <span className="answers-toc-count">{group.cards.length}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </nav>
      )}

      {visible.length === 0 && (
        <div className="staff-empty is-stacked answers-empty">
          <p>{t("staffConsole.answers.noMatch")}</p>
          <button className="secondary-button compact-button" type="button" onClick={() => { setQuery(""); setVoiceOnly(false); }}>
            {t("staffConsole.answers.clearFilters")}
          </button>
        </div>
      )}

      {chapters.map((group) => (
        <section className="answers-chapter" id={group.anchor} key={group.anchor}>
          <h4 className="answers-heading">{chapterTitle(group, t)}</h4>
          <div className="staff-card-list human-answers">
            {group.cards.map((card) => (
              <AnswerCard card={card} clientId={clientId} key={card.id} t={t} />
            ))}
          </div>
        </section>
      ))}

      {/* Unanswered questions used to sit among the answers wearing "Bez
          odpovědi" as if that were a reading. They are a list of what is
          missing, at the end, where a gap belongs. */}
      {unanswered.length > 0 && (
        <section className="answers-chapter answers-unanswered">
          <h4 className="answers-heading">{t("staffConsole.answers.unansweredTitle")}</h4>
          <p className="answers-unanswered-hint">{t("staffConsole.answers.unansweredHint")}</p>
          {/* The number is a place in ONE questionnaire, so a mixed list has to
              say which: "12" and "38" side by side name nothing the partner
              could repeat to the client. Caught by looking at the shot. */}
          {[...new Set(unanswered.map((card) => card.questionnaire))].map((questionnaire) => (
            <div key={questionnaire}>
              {!props.questionnaire && (
                <p className="answers-toc-title">{makeSafeT(t)(`sections.${questionnaire}.title`, questionnaire)}</p>
              )}
              <ol className="answers-unanswered-list">
                {unanswered.filter((card) => card.questionnaire === questionnaire).map((card) => (
                  <li key={card.id} data-answer-key={card.itemKey}>
                    <span className="answers-number" aria-hidden="true">{card.number}</span>
                    {card.label}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </section>
      )}
    </section>
  );
}

/** Exported for the guard: the catalog a scoped view reads. */
export { answerCatalogFor };
